'use client';

import React, { useState, useMemo, useCallback, useEffect, useRef, UIEvent } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  Tab,
  Spinner,
  Link,
  ScrollShadow,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { Typography, UniversalTable, ResponsiveModal } from '../../../../components';
import { CalendarIcon, EmptyContentIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { TableColumn } from '../../../../types/account';
import {
  partnerSfsActivityService,
  SfsTagData,
} from '../../../../api/partner/partnerRevshareService';
import axios from 'axios';
import { showError } from '../../../../utils/toast';

const PAGE_SIZE = 10;

interface SfsActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
  ofModelId?: number;
}

type ActivityType = 'incoming' | 'outgoing';

const SfsActivityModal: React.FC<SfsActivityModalProps> = ({
  isOpen,
  onClose,
  accountName,
  ofModelId,
}) => {
  const { t } = useTranslations();
  const [activityType, setActivityType] = useState<ActivityType>('incoming');

  // Incoming tags state (cursor-based pagination with lastId)
  const [incomingTags, setIncomingTags] = useState<SfsTagData[]>([]);
  const [incomingHasMore, setIncomingHasMore] = useState(false);
  const [incomingLastId, setIncomingLastId] = useState<number | undefined>(undefined);
  const [incomingLoading, setIncomingLoading] = useState(false);
  const [incomingLoadingMore, setIncomingLoadingMore] = useState(false);
  const [incomingPage, setIncomingPage] = useState(1);

  // Outgoing tags state (hasMore-based pagination)
  const [outgoingTags, setOutgoingTags] = useState<SfsTagData[]>([]);
  const [outgoingHasMore, setOutgoingHasMore] = useState(false);
  const [outgoingLoading, setOutgoingLoading] = useState(false);
  const [outgoingLoadingMore, setOutgoingLoadingMore] = useState(false);
  const [outgoingPage, setOutgoingPage] = useState(1);

  // Track loaded pages for outgoing
  const outgoingPagesLoaded = useRef<number>(0);

  // Fetch incoming tags (initial load) - handles empty responses with hasMore
  const fetchIncomingTags = useCallback(
    async (reset: boolean = true, fromId?: number) => {
      if (!ofModelId) return;

      if (reset) {
        setIncomingLoading(true);
        setIncomingTags([]);
        setIncomingLastId(undefined);
      } else {
        setIncomingLoadingMore(true);
      }

      try {
        let currentFromId = fromId ?? (reset ? undefined : incomingLastId);
        let allNewItems: SfsTagData[] = [];
        let hasMore = true;
        let lastId: number | undefined;
        const MAX_EMPTY_ITERATIONS = 5;
        let emptyIterations = 0;

        // Keep fetching while we get empty responses but hasMore is true
        while (hasMore && emptyIterations < MAX_EMPTY_ITERATIONS) {
          const response = await partnerSfsActivityService.getIntoTags({
            of_model_id: ofModelId,
            page_size: PAGE_SIZE,
            from_id: currentFromId,
          });

          const items = response.list || [];
          allNewItems = [...allNewItems, ...items];
          hasMore = response.hasMore || false;
          const newLastId = response.lastId;

          // If we got items or there's no more data, stop
          if (items.length > 0 || !hasMore) {
            lastId = newLastId;
            break;
          }

          // Safety check: if lastId didn't change, stop to prevent infinite loop
          if (newLastId === currentFromId || newLastId === undefined) {
            hasMore = false;
            break;
          }

          // Empty response but hasMore - continue with new lastId
          lastId = newLastId;
          currentFromId = newLastId;
          emptyIterations++;
        }

        if (reset) {
          setIncomingTags(allNewItems);
        } else {
          setIncomingTags((prev) => [...prev, ...allNewItems]);
        }
        setIncomingHasMore(hasMore);
        setIncomingLastId(lastId);
      } catch (err) {
        console.error('Error fetching incoming tags:', err);
        if (axios.isAxiosError(err) && err.response?.status === 400 && err.response?.data?.detail) {
          showError(err.response.data.detail);
        }
        if (reset) {
          setIncomingTags([]);
        }
      } finally {
        setIncomingLoading(false);
        setIncomingLoadingMore(false);
      }
    },
    [ofModelId, incomingLastId]
  );

  // Fetch outgoing tags - handles empty responses with hasMore
  const fetchOutgoingTags = useCallback(
    async (reset: boolean = true, startPage?: number) => {
      if (!ofModelId) return;

      const pageToLoad = startPage ?? (reset ? 1 : outgoingPage + 1);

      if (reset) {
        setOutgoingLoading(true);
        setOutgoingTags([]);
        outgoingPagesLoaded.current = 0;
      } else {
        setOutgoingLoadingMore(true);
      }

      try {
        let currentPage = pageToLoad;
        let allNewItems: SfsTagData[] = [];
        let hasMore = true;
        const MAX_EMPTY_ITERATIONS = 5;
        let emptyIterations = 0;

        // Keep fetching while we get empty responses but hasMore is true
        while (hasMore && emptyIterations < MAX_EMPTY_ITERATIONS) {
          const response = await partnerSfsActivityService.getOutTags({
            of_model_id: ofModelId,
            page: currentPage,
            page_size: PAGE_SIZE,
          });

          const items = response.items || [];
          allNewItems = [...allNewItems, ...items];
          hasMore = response.hasMore || false;

          // If we got items or there's no more data, stop
          if (items.length > 0 || !hasMore) {
            break;
          }

          // Empty response but hasMore - continue with next page
          currentPage++;
          emptyIterations++;
        }

        if (reset) {
          setOutgoingTags(allNewItems);
          setOutgoingPage(currentPage);
          outgoingPagesLoaded.current = currentPage;
        } else {
          setOutgoingTags((prev) => [...prev, ...allNewItems]);
          setOutgoingPage(currentPage);
          outgoingPagesLoaded.current = currentPage;
        }
        setOutgoingHasMore(hasMore);
      } catch (err) {
        console.error('Error fetching outgoing tags:', err);
        if (axios.isAxiosError(err) && err.response?.status === 400 && err.response?.data?.detail) {
          showError(err.response.data.detail);
        }
        if (reset) {
          setOutgoingTags([]);
        }
      } finally {
        setOutgoingLoading(false);
        setOutgoingLoadingMore(false);
      }
    },
    [ofModelId, outgoingPage]
  );

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && ofModelId) {
      if (activityType === 'incoming' && incomingTags.length === 0) {
        fetchIncomingTags(true);
      } else if (activityType === 'outgoing' && outgoingTags.length === 0) {
        fetchOutgoingTags(true);
      }
    }
  }, [isOpen, ofModelId, activityType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActivityType('incoming');
      setIncomingTags([]);
      setOutgoingTags([]);
      setIncomingPage(1);
      setOutgoingPage(1);
      setIncomingLastId(undefined);
      setIncomingHasMore(false);
      setOutgoingHasMore(false);
      outgoingPagesLoaded.current = 0;
    }
  }, [isOpen]);

  const handleActivityTypeChange = useCallback(
    (key: string | number) => {
      const newType = key as ActivityType;
      setActivityType(newType);
      // Fetch data for the new tab if not already loaded
      if (newType === 'incoming' && incomingTags.length === 0) {
        fetchIncomingTags(true);
      } else if (newType === 'outgoing' && outgoingTags.length === 0) {
        fetchOutgoingTags(true);
      }
    },
    [incomingTags.length, outgoingTags.length]
  );

  // Load more handlers
  const handleLoadMoreIncoming = useCallback(() => {
    if (incomingHasMore && !incomingLoadingMore) {
      fetchIncomingTags(false);
    }
  }, [incomingHasMore, incomingLoadingMore, fetchIncomingTags]);

  const handleLoadMoreOutgoing = useCallback(() => {
    if (outgoingHasMore && !outgoingLoadingMore) {
      fetchOutgoingTags(false);
    }
  }, [outgoingHasMore, outgoingLoadingMore, fetchOutgoingTags]);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isNearBottom) {
        if (activityType === 'incoming') {
          handleLoadMoreIncoming();
        } else {
          handleLoadMoreOutgoing();
        }
      }
    },
    [activityType, handleLoadMoreIncoming, handleLoadMoreOutgoing]
  );

  // Columns for both tables
  const columns: TableColumn[] = useMemo(
    () => [
      {
        name: t('partner.revshare.accounts.sfsModal.account'),
        key: 'account',
        width: '200px',
      },
      {
        name: t('partner.revshare.accounts.sfsModal.username'),
        key: 'username',
        width: '150px',
      },
      {
        name: t('partner.revshare.accounts.sfsModal.date'),
        key: 'date',
        width: '150px',
      },
    ],
    [t]
  );

  const renderCell = useCallback((item: SfsTagData, columnKey: string) => {
    switch (columnKey) {
      case 'account':
        return (
          <div className="flex items-center gap-2">
            {item.avatar && (
              <img
                src={item.avatar}
                alt={item.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            )}
            <Typography variant="body2" className="text-[#11181C]">
              {item.name || '-'}
            </Typography>
          </div>
        );
      case 'username':
        return (
          <Link
            href={`https://onlyfans.com/${item.user_name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#006FEE] hover:underline"
          >
            <Typography variant="body2" className="!text-[#006FEE]">
              @{item.user_name}
            </Typography>
          </Link>
        );
      case 'date':
        return (
          <div className="flex items-center gap-1">
            <CalendarIcon />
            <Typography variant="body2" className="text-[#71717A]">
              {new Date(item.created_at).toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  }, []);

  const emptyContent = (
    <div className="flex flex-col items-center justify-center py-8 gap-4">
      <EmptyContentIc />
      <Typography variant="h6" className="!font-semibold text-auth-ink text-center">
        {t('partner.revshare.accounts.sfsModal.empty')}
      </Typography>
    </div>
  );

  const isLoading = activityType === 'incoming' ? incomingLoading : outgoingLoading;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      hideCloseButton
      classNames={{
        base: 'md:max-w-[50rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.06)]">
        <ModalHeader className="flex justify-between items-center px-4 md:px-6 pt-6 pb-4 relative">
          <Typography variant="h4" className="!font-medium !text-xl text-auth-ink">
            {t('partner.revshare.accounts.sfsModal.title')} {accountName}
          </Typography>
          <button
            onClick={onClose}
            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-2 hover:bg-gray-100"
          >
            <Icon icon="lucide:x" className="w-4 h-4 text-auth-muted" />
          </button>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-3 flex flex-col overflow-hidden">
          {/* Tabs for switching between incoming and outgoing */}
          <div className="bg-gray-100 rounded-3 w-fit mb-4 flex-shrink-0">
            <Tabs
              selectedKey={activityType}
              onSelectionChange={handleActivityTypeChange}
              variant="light"
              classNames={{
                tabList: 'gap-1',
                cursor: 'bg-white rounded-lg shadow-sm',
                tab: 'px-3 py-1 text-sm font-normal data-[selected=true]:text-black text-gray-500',
                tabContent: 'group-data-[selected=true]:text-black',
              }}
            >
              <Tab key="incoming" title={t('partner.revshare.accounts.sfsModal.incomingTab')} />
              <Tab key="outgoing" title={t('partner.revshare.accounts.sfsModal.outgoingTab')} />
            </Tabs>
          </div>

          {/* Scrollable content area */}
          <ScrollShadow
            className="flex-1 md:max-h-[25rem] ms:overflow-y-auto"
            onScroll={handleScroll}
          >
            {/* Loading state */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner
                  size="lg"
                  classNames={{
                    circle1: '!border-b-[#006FEE]',
                    circle2: '!border-b-[#006FEE]/50',
                  }}
                />
              </div>
            ) : (
              <>
                {/* Incoming tags table */}
                {activityType === 'incoming' && (
                  <>
                    <UniversalTable<SfsTagData>
                      data={incomingTags}
                      columns={columns}
                      renderCell={renderCell}
                      emptyContent={emptyContent}
                    />
                    {incomingLoadingMore && (
                      <div className="flex justify-center py-4">
                        <Spinner
                          size="sm"
                          classNames={{
                            circle1: '!border-b-[#006FEE]',
                            circle2: '!border-b-[#006FEE]/50',
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Outgoing tags table */}
                {activityType === 'outgoing' && (
                  <>
                    <UniversalTable<SfsTagData>
                      data={outgoingTags}
                      columns={columns}
                      renderCell={renderCell}
                      emptyContent={emptyContent}
                    />
                    {outgoingLoadingMore && (
                      <div className="flex justify-center py-4">
                        <Spinner
                          size="sm"
                          classNames={{
                            circle1: '!border-b-[#006FEE]',
                            circle2: '!border-b-[#006FEE]/50',
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </ScrollShadow>
        </ModalBody>

        <ModalFooter className="px-4 md:px-6 py-4 flex justify-end">
          <Button className="bg-partner h-10 px-3.5 py-2.5 rounded-3" onPress={onClose}>
            <Typography variant="body2" className="!text-white font-semibold">
              {t('partner.revshare.accounts.sfsModal.close')}
            </Typography>
          </Button>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default SfsActivityModal;
